import { IonModal } from "@ionic/react";
import { useFormik } from "formik";
import { CSSProperties, FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import * as Yup from "yup";
import { TextField } from "../../components/atoms/LoginTextField";
import { useAuth } from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/ConfigContext";
import useToast from "../../hooks/useToast";
import { cancelPhoneNumberChange, requestPhoneNumberChange, resendPhoneNumberChangeCode, verifyPhoneNumberChange } from "../../services/user.service";
import { otpChallengeStatusResponse } from "../../types/user.types";

interface Props {
  isOpen: boolean;
  onDismiss(): void;
  hasExistingPhoneNumber: boolean;
}

interface ChangePhoneNumberFormType {
  newPhoneNumber: string;
  currentPassword: string;
}

type Step = "request" | "verify";

const changePhoneNumberSchema = Yup.object().shape({
  newPhoneNumber: Yup.string()
    .matches(/^(06|07)[0-9]{8}$/, "profile_change_phone_invalid_number")
    .required("common_all_fields_required"),
  currentPassword: Yup.string().required("common_all_fields_required"),
});

const ChangePhoneNumberModal: FC<Props> = ({ isOpen, onDismiss, hasExistingPhoneNumber }) => {
  const { t } = useTranslation();
  const { user, getCurrent } = useAuth();
  const { study } = useConfig();
  const present = useToast();
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("request");
  const [verificationCode, setVerificationCode] = useState("");
  const [otpStatus, setOtpStatus] = useState<otpChallengeStatusResponse | null>(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [isNewPhoneNumberFieldUnlocked, setIsNewPhoneNumberFieldUnlocked] = useState(false);
  const [isCurrentPasswordFieldUnlocked, setIsCurrentPasswordFieldUnlocked] = useState(false);
  const [isVerificationCodeFieldUnlocked, setIsVerificationCodeFieldUnlocked] = useState(false);
  const titleKey = hasExistingPhoneNumber ? "profile_change_phone_title" : "profile_add_phone_title";
  const descriptionKey = hasExistingPhoneNumber
    ? "profile_change_phone_description"
    : "profile_add_phone_description";
  const pendingPhoneNumber = useMemo(() => (user?.pending_phone_number || "").replace(/\s+/g, "").trim(), [user?.pending_phone_number]);

  const mapRequestError = (error: any): string => {
    const responseMessage = String(error?.response?.data?.message || "").toLowerCase();

    if (responseMessage.includes("phone number already exists")) {
      return "profile_change_phone_exists";
    }

    if (error?.response?.status === 401) {
      return "profile_change_phone_invalid_password";
    }

    if (responseMessage.includes("new phone number must be different from current phone number")) {
      return "profile_change_phone_same_phone";
    }

    return "profile_change_phone_error";
  };

  const mapVerificationError = (error: any): string => {
    const responseMessage = String(error?.response?.data?.message || "").toLowerCase();

    if (responseMessage.includes("invalid verification code")) {
      return "profile_verification_code_invalid";
    }

    if (responseMessage.includes("has expired")) {
      return "profile_verification_code_expired";
    }

    if (responseMessage.includes("too many failed attempts")) {
      return "profile_verification_code_too_many_attempts";
    }

    if (error?.response?.status === 429 || responseMessage.includes("resend is not available yet")) {
      return "profile_verification_code_resend_rate_limited";
    }

    if (responseMessage.includes("phone number already exists")) {
      return "profile_change_phone_exists";
    }

    return "profile_change_phone_error";
  };

  const handleSubmit = async (values: ChangePhoneNumberFormType) => {
    try {
      setErrorKey(null);

      const { data } = await requestPhoneNumberChange(
        { studyId: study.id },
        {
          new_phone_number: values.newPhoneNumber,
          current_password: values.currentPassword,
        }
      );

      await getCurrent();
      setOtpStatus(data);
      setVerificationCode("");
      setStep("verify");
    } catch (error: any) {
      setErrorKey(mapRequestError(error));
    }
  };

  const { values, touched, errors, submitForm, setFieldValue, resetForm } =
    useFormik<ChangePhoneNumberFormType>({
      initialValues: {
        newPhoneNumber: "",
        currentPassword: "",
      },
      onSubmit: handleSubmit,
      validationSchema: changePhoneNumberSchema,
    });

  const currentTargetValue = (pendingPhoneNumber || values.newPhoneNumber).trim();
  const hasPendingPhoneNumberChange = Boolean(pendingPhoneNumber);
  const isBusy = isRequesting || isVerifying || isResending || isDiscarding;

  const shouldResetPendingRequest = (error: any): boolean => {
    const responseMessage = String(error?.response?.data?.message || "").toLowerCase();

    return (
      responseMessage.includes("has expired") ||
      responseMessage.includes("no longer valid") ||
      responseMessage.includes("too many failed attempts") ||
      responseMessage.includes("verification code not found") ||
      responseMessage.includes("no pending phone number change found")
    );
  };

  const resetExpiredPendingRequest = async (nextErrorKey: string | null = null) => {
    resetForm({
      values: {
        newPhoneNumber: "",
        currentPassword: "",
      },
    });
    setStep("request");
    setVerificationCode("");
    setOtpStatus(null);
    setIsRequesting(false);
    setIsVerifying(false);
    setIsResending(false);
    setErrorKey(nextErrorKey);
    await getCurrent();
  };

  const handleVerifySubmit = async () => {
    if (isVerifying || isDiscarding) {
      return;
    }

    if (!verificationCode.trim()) {
      setErrorKey("common_all_fields_required");
      return;
    }

    try {
      setIsVerifying(true);
      setErrorKey(null);
      await verifyPhoneNumberChange(
        { studyId: study.id },
        { code: verificationCode.replace(/\s+/g, "") }
      );

      await getCurrent();
      present(t("profile_change_phone_verified"));
      handleCloseAfterSuccess();
    } catch (error: any) {
      const nextErrorKey = mapVerificationError(error);

      if (shouldResetPendingRequest(error)) {
        await resetExpiredPendingRequest(nextErrorKey);
        return;
      }

      setErrorKey(nextErrorKey);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (isResending || isDiscarding) {
      return;
    }

    try {
      setIsResending(true);
      setErrorKey(null);
      const { data } = await resendPhoneNumberChangeCode({ studyId: study.id });
      await getCurrent();
      setOtpStatus(data);
      setVerificationCode("");
    } catch (error: any) {
      const nextErrorKey = mapVerificationError(error);

      if (shouldResetPendingRequest(error)) {
        await resetExpiredPendingRequest(nextErrorKey);
        return;
      }

      setErrorKey(nextErrorKey);
    } finally {
      setIsResending(false);
    }
  };

  const handleRequestSubmit = async () => {
    if (isRequesting || isDiscarding) {
      return;
    }

    try {
      setIsRequesting(true);
      await submitForm();
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDiscardPendingChange = async () => {
    if (!hasPendingPhoneNumberChange || isBusy) {
      return;
    }

    try {
      setIsDiscarding(true);
      setErrorKey(null);
      await cancelPhoneNumberChange({ studyId: study.id });
      await getCurrent();
      resetForm({
        values: {
          newPhoneNumber: "",
          currentPassword: "",
        },
      });
      setStep("request");
      setVerificationCode("");
      setOtpStatus(null);
      setIsRequesting(false);
      setIsVerifying(false);
      setIsResending(false);
      setIsNewPhoneNumberFieldUnlocked(false);
      setIsCurrentPasswordFieldUnlocked(false);
      setIsVerificationCodeFieldUnlocked(false);
      present(t("profile_change_phone_cancelled"));
      onDismiss();
    } catch (error: any) {
      setErrorKey("profile_change_phone_discard_error");
    } finally {
      setIsDiscarding(false);
    }
  };

  const handleCancelAction = async () => {
    if (hasPendingPhoneNumberChange) {
      await handleDiscardPendingChange();
      return;
    }

    handleDismiss();
  };

  const handleCloseAfterSuccess = () => {
    resetForm({
      values: {
        newPhoneNumber: "",
        currentPassword: "",
      },
    });
    setErrorKey(null);
    setStep("request");
    setVerificationCode("");
    setOtpStatus(null);
    setIsRequesting(false);
    setIsVerifying(false);
    setIsResending(false);
    setIsDiscarding(false);
    setIsNewPhoneNumberFieldUnlocked(false);
    setIsCurrentPasswordFieldUnlocked(false);
    setIsVerificationCodeFieldUnlocked(false);
    onDismiss();
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    resetForm({
      values: {
        newPhoneNumber: pendingPhoneNumber,
        currentPassword: "",
      },
    });
    setErrorKey(null);
    setStep(pendingPhoneNumber ? "verify" : "request");
    setVerificationCode("");
    setOtpStatus(pendingPhoneNumber ? { expires_in_seconds: 0, resend_available_in_seconds: 0 } : null);
    setIsRequesting(false);
    setIsVerifying(false);
    setIsResending(false);
    setIsDiscarding(false);
    setIsNewPhoneNumberFieldUnlocked(false);
    setIsCurrentPasswordFieldUnlocked(false);
    setIsVerificationCodeFieldUnlocked(false);
  }, [isOpen, pendingPhoneNumber, resetForm]);

  useEffect(() => {
    if (!isOpen || !otpStatus) {
      return;
    }

    const timer = window.setInterval(() => {
      let shouldRefreshCurrentUser = false;

      setOtpStatus((currentStatus) => {
        if (!currentStatus) {
          return currentStatus;
        }

        const nextStatus = {
          expires_in_seconds: Math.max(currentStatus.expires_in_seconds - 1, 0),
          resend_available_in_seconds: Math.max(currentStatus.resend_available_in_seconds - 1, 0),
        };

        if (
          nextStatus.expires_in_seconds === currentStatus.expires_in_seconds &&
          nextStatus.resend_available_in_seconds === currentStatus.resend_available_in_seconds
        ) {
          window.clearInterval(timer);
        }

        if (currentStatus.expires_in_seconds > 0 && nextStatus.expires_in_seconds === 0) {
          shouldRefreshCurrentUser = true;
        }

        return nextStatus;
      });

      if (shouldRefreshCurrentUser) {
        window.clearInterval(timer);
        void resetExpiredPendingRequest();
      }
    }, 1000);

    return () => window.clearInterval(timer);
  }, [getCurrent, isOpen, Boolean(otpStatus)]);

  const handleDismiss = () => {
    resetForm({
      values: {
        newPhoneNumber: pendingPhoneNumber,
        currentPassword: "",
      },
    });
    setErrorKey(null);
    setStep(pendingPhoneNumber ? "verify" : "request");
    setVerificationCode("");
    setOtpStatus(pendingPhoneNumber ? { expires_in_seconds: 0, resend_available_in_seconds: 0 } : null);
    setIsRequesting(false);
    setIsVerifying(false);
    setIsResending(false);
    setIsDiscarding(false);
    setIsNewPhoneNumberFieldUnlocked(false);
    setIsCurrentPasswordFieldUnlocked(false);
    setIsVerificationCodeFieldUnlocked(false);
    onDismiss();
  };

  const resendLabel = otpStatus?.resend_available_in_seconds
    ? t("profile_verification_code_resend_in", { seconds: otpStatus.resend_available_in_seconds })
    : t("profile_verification_code_resend");

  const verifyDescription = currentTargetValue
    ? t("profile_change_phone_code_description", { value: currentTargetValue })
    : t("profile_change_phone_code_description_fallback");
  const stepDescription = step === "request" ? t(descriptionKey) : verifyDescription;

  return (
    <IonModal isOpen={isOpen} backdropDismiss={!isDiscarding} onWillDismiss={handleDismiss}>
      <Container onClick={handleDismiss}>
        <Card
          as="form"
          autoComplete="off"
          onSubmit={(event) => {
            event.preventDefault();
            if (step === "request") {
              submitForm();
              return;
            }

            handleVerifySubmit();
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <Title>{t(titleKey)}</Title>
          <Description>{stepDescription}</Description>

          {step === "request" ? (
            <>
              <TextField
                value={values.newPhoneNumber}
                type="tel"
                error={touched.newPhoneNumber && errors.newPhoneNumber}
                onChange={(value) => setFieldValue("newPhoneNumber", value.replace(/\s+/g, ""))}
                label={t("profile_phone")}
                placeholder={t("profile_phone")}
                autoComplete="off"
                inputProps={{
                  name: "profile-phone-value",
                  readOnly: !isNewPhoneNumberFieldUnlocked,
                  onFocus: () => setIsNewPhoneNumberFieldUnlocked(true),
                  onPointerDown: () => setIsNewPhoneNumberFieldUnlocked(true),
                  inputMode: "tel",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                  maxLength: 10,
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                  "data-bwignore": "true",
                  "data-form-type": "other",
                }}
              />

              <TextField
                value={values.currentPassword}
                type="text"
                error={touched.currentPassword && errors.currentPassword}
                onChange={(value) => setFieldValue("currentPassword", value)}
                label={t("profile_password")}
                placeholder={t("profile_password")}
                autoComplete="off"
                inputProps={{
                  name: "profile-phone-verification-value",
                  readOnly: !isCurrentPasswordFieldUnlocked,
                  onFocus: () => setIsCurrentPasswordFieldUnlocked(true),
                  onPointerDown: () => setIsCurrentPasswordFieldUnlocked(true),
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                  style: { WebkitTextSecurity: "disc" } as CSSProperties,
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                  "data-bwignore": "true",
                  "data-form-type": "other",
                }}
              />
            </>
          ) : (
            <>
              <TextField
                value={verificationCode}
                type="tel"
                error={undefined}
                onChange={(value) => setVerificationCode(value.replace(/\D/g, "").slice(0, 6))}
                label={t("profile_verification_code")}
                placeholder={t("profile_verification_code_placeholder")}
                autoComplete="off"
                inputProps={{
                  name: "profile-phone-change-verification-code",
                  readOnly: !isVerificationCodeFieldUnlocked,
                  onFocus: () => setIsVerificationCodeFieldUnlocked(true),
                  onPointerDown: () => setIsVerificationCodeFieldUnlocked(true),
                  inputMode: "numeric",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                  maxLength: 6,
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                  "data-bwignore": "true",
                  "data-form-type": "other",
                }}
              />

              <VerificationMeta>
                {otpStatus?.expires_in_seconds
                  ? t("profile_verification_code_expires_in", { minutes: Math.ceil(otpStatus.expires_in_seconds / 60) })
                  : t("profile_verification_code_expired_hint")}
              </VerificationMeta>

              <ResendButton
                type="button"
                onClick={handleResendCode}
                disabled={isResending || Boolean(otpStatus?.resend_available_in_seconds)}
              >
                {isResending ? t("profile_action_processing") : resendLabel}
              </ResendButton>
            </>
          )}

          {errorKey && <Error>{t(errorKey)}</Error>}

          <Buttons>
            <ModalButton type="button" onClick={handleCancelAction} disabled={isBusy}>
              {t("common_cancel")}
            </ModalButton>
            <PrimaryModalButton
              type="button"
              onClick={step === "request" ? handleRequestSubmit : handleVerifySubmit}
              disabled={isBusy}
            >
              {t(
                step === "request"
                  ? isRequesting
                    ? "profile_action_processing"
                    : "common_next"
                  : isVerifying
                    ? "profile_action_processing"
                    : "profile_verification_code_verify"
              )}
            </PrimaryModalButton>
          </Buttons>
        </Card>
      </Container>
    </IonModal>
  );
};

const Container = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  width: 100%;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
  padding: 24px 20px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
`;

const Description = styled.p`
  color: #5f5f5f;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 16px;
  text-align: center;
`;

const VerificationMeta = styled.div`
  color: #6b7280;
  font-size: 13px;
  margin-top: 8px;
`;

const ResendButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  border: 1px solid ${(props) => props.theme.primary.main};
  border-radius: 999px;
  background: white;
  color: ${(props) => props.theme.primary.main};
  font-size: 14px;
  font-weight: 600;
  margin-top: 12px;
  padding: 0 14px;
  cursor: pointer;

  &:disabled {
    border-color: #d1d5db;
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const Error = styled.div`
  color: ${(props) => props.theme.danger.main};
  margin-top: 10px;
  font-size: 15px;
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 20px;
`;

const ModalButton = styled.button`
  border: none;
  background: transparent;
  padding: 10px 20px;
  color: ${(props) => props.theme.primary.main};

  &:disabled {
    color: #9ca3af;
  }
`;

const PrimaryModalButton = styled(ModalButton)`
  font-weight: 600;
`;

export default ChangePhoneNumberModal;